import { IDeliveryOption } from "../../api/estore/checkout/interfaces/delivery-option-interface.js";
import { el, text } from "../../lib/redom.es.min.js";
import { BaseRedomElement } from "../../utils/base-redom-element.js";
import { newlineToBreakEscape } from "../../utils/string.js";
import { DeliveryPickupPointSelector } from "./delivery-pickuppoint-selector.js";
import { DeliverySelectorStateManager } from "./delivery-selector-state-manager.js";
import { ICmsDeliverySelectorConfiguration } from "./interfaces/cms-delivery-selector-config-interface.js";

export class DeliveryOption extends BaseRedomElement {
    config: ICmsDeliverySelectorConfiguration;
    stateManager: DeliverySelectorStateManager;
    option: IDeliveryOption;
    translations: any;
    root: Node;

    constructor(config: ICmsDeliverySelectorConfiguration, stateManager: DeliverySelectorStateManager, translations: any, option: IDeliveryOption) {
        super(config.useShadowRoot);
        this.config = config;
        this.root = config.useShadowRoot ? this.shadowRoot: this;
        this.stateManager = stateManager;
        this.translations = translations;
        this.option = option;
    }

    connectedCallback() {
        this.render();
        this.attachDelegates();
        this.setSelected(this.option.selected);
    }

    render() {
        const rel = this.element.bind(this);
        const o = this.option;

        // Short-hand vars
        const cbx = o.providerCheckbox;
        const cbxtt = cbx && o.providerCheckboxTooltip;
        const inp = o.providerInput;
        const pp = o.providerPickupPointSelector;
        const anyInp = cbx || inp || pp;
               
        const containers = [
            this.config.useShadowRoot ? el("link", { rel: "stylesheet", href: this.config.cssFiles.optionItem}) : null,
            el("a.d-flex", {onclick: this.clicked.bind(this)},
                el("div.delivery-option",
                    rel("radio", "input.delivery-option", { type: "radio", name: "delivery-option", value: o.id, checked: o.selected, onclick: this.clicked.bind(this) }),
                ),
                el("header.d-flex",                    
                    el("div.title-and-price.d-flex.flex-column.flex-sm-row.flex-wrap.justify-content-between",
                        el("div.title-cont.d-flex.flex-column",
                            el("div", 
                                o.iconUrl && el("img.option-icon", {src: o.iconUrl}),
                            ),
                            el("h4", o.title)
                        ),
                        el("div.price-cont.d-flex.flex-column.flex-sm-row-reverse.align-items-sm-center.justify-content-between",
                            o.price && el("span.price", o.price),
                            o.deliveryTimeDescription && el("span.delivery-time", o.deliveryTimeDescription)
                        )
                    ),
                )                
            ),
            el("div.content",                
                el("p.description",{ innerHTML: newlineToBreakEscape(o.description) } ),
                (anyInp || o.providerInfo) && rel("inputs", "div.inputs",
                    o.providerInfo && el("p.provider-info", { innerHTML: o.providerInfo } ),
                    el("div.d-flex.d-flex.align-items-center",
                        cbx && el("label.provider-check",
                            el("input.provider-check", { type: "checkbox", checked: o.providerCheckboxChecked, onchange: this.providerCheckChange.bind(this) }),
                            text(o.providerCheckboxText)
                        ),
                        cbxtt && el("img.info", {src: this.config.cssFiles.infoIcon, onclick: this.infoIconClicked.bind(this) }),
                    ),
                    cbxtt && rel("providerCheckTooltip", "p.provider-check-tooltip", o.providerCheckboxTooltip),
                    inp && rel("inputCont", "div.provider-input", { class: !o.providerInputActivateByCheckbox || o.providerCheckboxChecked ? "visible" : "" }, 
                        el("label",
                            el("span", o.providerInputLabel),
                            rel("providerInput", "input.providerInput.form-control", {placeholder: o.providerInputPlaceholder, value: o.providerInputValue ?? "", onkeyup: this.providerInputValueChange.bind(this)})
                        )
                    ),
                    pp && el("div.pickup-point",
                        el("label", this.translations.choosePickupPoint),
                        new DeliveryPickupPointSelector(this.config, this.stateManager, o.id, o.providerPickupPoints)
                    )
                ),
                o.mapEmbed && el("div.map", { innerHTML: o.mapEmbed })
            )
        ];

        this.setAttribute("data-id", o.id);

        this.mountArray(this.root, containers);

        if (this.config.triggerOptionSelectedOnInit && this.option.selected) {
            this.stateManager.setSelectedOption(this.option.id);
        }
    }

    attachDelegates() {
        const e = this.elements;

        // Option selected state changed
        this.stateManager.selectedOptionDelegates.push((_, newOptionId) => {
            const selected = this.option.id === newOptionId;
            this.option.selected = selected;
            this.setSelected(selected);
        });

        // Checkbox clicked
        this.stateManager.checkedProviderCheckboxDelegates.push(
            (optionId, _, newState) => {
                if (optionId === this.option.id) {
                    newState ? 
                        e.inputCont?.classList.add("visible") : 
                        this.option.providerInputActivateByCheckbox && e.inputCont?.classList.remove("visible");
                }
            });
        
        // Checkbox info clicked
        this.stateManager.onProviderCheckboxInfoVisibleChangeDelegates.push(
            (optionId, _, newState) => {
                if (optionId === this.option.id) {
                    newState ? 
                        e.providerCheckTooltip.classList.add("visible") : 
                        e.providerCheckTooltip.classList.remove("visible");
                }
            });
    }

    setSelected(selected: boolean) {
        const e = this.elements;
        if (selected) {
            this.classList.add("selected");
            e.inputs?.classList.add("visible");
            // Need a timeout for this because there is event order collision
            window.setTimeout(() => e.radio.checked = true, 1); 
        } else {
            this.classList.remove("selected");
            e.inputs?.classList.remove("visible");
            e.radio.checked = false; 
        }
    }

    // Event handlers
    clicked(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();
        this.stateManager.setSelectedOption(this.option.id);
    }

    providerCheckChange(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        const cbx = event.target as HTMLInputElement;

        this.stateManager.setProviderCheckboxState(this.option.id, cbx.checked);
    }

    infoIconClicked(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        this.stateManager.toggleProviderCheckboxInfoVisible(this.option.id);
    }

    providerInputKeyTimeout: number = null;
    providerInputValueChange(event: Event) {
        event?.preventDefault();
        event?.stopPropagation();

        const inp = event.target as HTMLInputElement;

        if (this.providerInputKeyTimeout) {
            clearTimeout(this.providerInputKeyTimeout);
        }

        this.providerInputKeyTimeout = setTimeout(() => this.stateManager.setProviderInputValue(this.option.id, inp.value), 400) as unknown as number;
    }
    // #Event handlers
}

customElements.define("delivery-option", DeliveryOption);